// Generated by tutorials/record.cjs from tutorials/articles/h02-ehr-supplements-tube-feeds.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h02 --text-only` (wording) or
// `node tutorials/record.cjs h02` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'ehr-supplements-tube-feeds',
  catalogId: 'h02',
  title: 'Automatic supplements and tube feeds from your EHR',
  summary: 'When it is turned on for your facility, the supplement and tube feed orders your EHR sends are added to each resident for you, and ended when the order stops. Here is what you will see, what Dietech never touches, and how to turn it on or off.',
  audience: 'Dietary managers and dietitians at facilities whose EHR sends supplement and tube feed orders',
  area: 'residents',
  learn: [
    'Where supplements and tube feeds from your EHR show up on a resident',
    'What happens when your EHR stops an order',
    'What Dietech never changes',
    'How to review what was added for each resident',
    'How to map a supplement or a tube feed, and set the mealtimes for daily, BID and TID',
    'How to turn it on, change it or turn it off',
  ],
  keywords: [
    'ehr',
    'emr',
    'supplement',
    'supplements',
    'nourishment',
    'tube feed',
    'tube feeding',
    'enteral',
    'meal pattern',
    'global meal pattern',
    'bid',
    'tid',
    'daily',
    'frequency',
    'automatic',
  ],
  steps: [
    {
      id: 'supplements-on-meal-pattern',
      part: 'What you see on the resident',
      heading: 'Supplements appear on the Meal Pattern',
      text: 'When your EHR orders a supplement that is mapped, Dietech adds it to the resident\'s Meal Pattern at the mealtimes your facility set for its frequency. "Juven BID" becomes Juven at Lunch and at Dinner.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/supplements-on-meal-pattern.webp',
          width: 1280,
          height: 462,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/supplements-on-meal-pattern.mp4',
          width: 1280,
          height: 462,
          durationSec: 4.1,
        },
        alt: 'A resident\'s Meal Pattern with Juven listed at Lunch and at Dinner.',
      },
    },
    {
      id: 'globe',
      part: 'What you see on the resident',
      heading: 'The globe marks them',
      text: 'Items from your EHR are global meal pattern items, so they show the globe icon. Each one also has a calendar icon at the end of its row: point to it to see the day the order started.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/globe.webp',
          width: 1280,
          height: 462,
        },
        alt: 'The Juven row at Lunch, marked with the globe icon.',
      },
    },
    {
      id: 'order-stops',
      part: 'What you see on the resident',
      heading: 'When the order stops, the item ends',
      text: 'When your EHR stops an order, Dietech gives the item an end date instead of deleting it. Fortified Pudding here was stopped, so its calendar icon shows the day it started and the day it ended.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/order-stops.webp',
          width: 1280,
          height: 462,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/order-stops.mp4',
          width: 1280,
          height: 462,
          durationSec: 3.2,
        },
        alt: 'The Fortified Pudding row at the HS snack, with the note on its calendar icon showing the dates it was active.',
      },
    },
    {
      id: 'tube-feed-row',
      part: 'What you see on the resident',
      heading: 'A tube feed order becomes a tube feed row',
      text: 'For a tube feed order, Dietech keeps one row on the resident\'s Tube Feed. Its comments start with "From EHR" and give the order as your EHR sent it. When the order stops, Dietech removes the row it added.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/tube-feed-row.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/tube-feed-row.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'The Tube Feed section with one row: Standard 1.2 Cal Formula, 1100 cc over 20 hours, with the comment From EHR.',
      },
    },
    {
      id: 'tube-feed-box',
      part: 'What you see on the resident',
      heading: 'The Tube Feed box is checked',
      text: 'When Dietech adds a tube feed, it also checks the resident\'s Tube Feed box, and TF shows next to their name. When the order stops it clears the box, unless someone on your staff changed it in the meantime.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/tube-feed-box.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The TF badge next to the resident\'s name at the top of the page.',
      },
    },
    {
      id: 'staff-items',
      part: 'What Dietech never touches',
      heading: 'Items your staff added stay as they are',
      text: 'Dietech only changes or ends the items it added. Coffee, oatmeal and ice cream here were added by staff, so your EHR never changes or ends them.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/staff-items.webp',
          width: 1280,
          height: 462,
        },
        alt: 'The Meal Pattern with the staff-added Coffee, Oatmeal and Vanilla Ice Cream rows.',
      },
    },
    {
      id: 'staff-changes',
      part: 'What Dietech never touches',
      heading: 'Change an EHR item and it becomes yours',
      text: 'If someone on your staff edits, ends or removes an item your EHR added, Dietech stops managing it and leaves it exactly as your staff set it.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/staff-changes.webp',
          width: 1280,
          height: 930,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/staff-changes.mp4',
          width: 1280,
          height: 930,
          durationSec: 2,
        },
        alt: 'The Juven item at Dinner opened for editing from the Meal Pattern.',
      },
    },
    {
      id: 'added-items',
      part: 'Review what was added',
      heading: 'Check Added Items',
      text: 'On the EHR page, Added Items lists everything your EHR added to or ended for a resident: each supplement at its mealtime, each tube feed, and anything it found already there. The number on the tab is how many are waiting for your review.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/added-items.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/added-items.mp4',
          width: 1280,
          height: 684,
          durationSec: 2.9,
        },
        alt: 'The Added Items tab listing Juven at Lunch and at Dinner, Fortified Pudding ended, a tube feed, and a protein shake that was already there.',
      },
    },
    {
      id: 'added-items-reviewed',
      part: 'Review what was added',
      heading: 'Mark them reviewed',
      text: 'Check the ones you have reviewed and select Mark Reviewed. Your name and the time are recorded.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/added-items-reviewed.webp',
          width: 1280,
          height: 684,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/added-items-reviewed.mp4',
          width: 1280,
          height: 684,
          durationSec: 6,
        },
        alt: 'Two rows checked, and a note that they were marked reviewed.',
      },
    },
    {
      id: 'frequency-mealtimes',
      part: 'Map supplements and tube feeds',
      heading: 'Pick the mealtime for "daily"',
      text: 'Select Mappings, then Map waiting text. Supplement text waits in its own group, Supplements and tube feeds waiting.\n\nThe first time an order says "daily", pick which mealtime daily means at your facility. Nothing is picked for you. Then Save and next.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/frequency-mealtimes.webp',
          width: 1280,
          height: 746,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/frequency-mealtimes.mp4',
          width: 1280,
          height: 746,
          durationSec: 3.8,
        },
        alt: 'The Map this pop-up for "DAILY", in the group Supplements and tube feeds waiting, with Lunch checked.',
      },
    },
    {
      id: 'map-supplement',
      part: 'Map supplements and tube feeds',
      heading: 'Map a supplement',
      text: 'For supplement text such as "Protein shake BID", Dietech suggests the supplement from your global meal patterns, and Text to match shows the part it matches on. Check what it gives, then Save. From then on, every order for it is added for you.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/map-supplement.webp',
          width: 1280,
          height: 1190,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/map-supplement.mp4',
          width: 1280,
          height: 1190,
          durationSec: 3.5,
        },
        alt: 'The Map this pop-up for "Protein shake BID", with Supplement picked and the PROTEIN SHAKE global meal pattern suggested.',
      },
    },
    {
      id: 'supplement-mappings',
      part: 'Map supplements and tube feeds',
      heading: 'See and change supplement mappings',
      text: 'Back on the Mappings tab, Supplements lists every supplement mapping and what it gives. Select one and Edit to change the global meal pattern or the mealtimes it uses.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/supplement-mappings.webp',
          width: 942,
          height: 1270,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/supplement-mappings.mp4',
          width: 942,
          height: 1270,
          durationSec: 2,
        },
        alt: 'The editor for the JUVEN supplement mapping, which gives the Juven global meal pattern at the order\'s frequency.',
      },
    },
    {
      id: 'map-tube-feed',
      part: 'Map supplements and tube feeds',
      heading: 'Map a tube feed formula',
      text: 'Under Tube Feeds, map each formula name your EHR sends to the product in your Tube Feed list. You can also set the hours, flush and tube type to use when the order leaves them out.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/map-tube-feed.webp',
          width: 942,
          height: 1102,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/map-tube-feed.mp4',
          width: 942,
          height: 1102,
          durationSec: 2,
        },
        alt: 'The editor for the STANDARD 1.2 CAL FORMULA tube feed mapping, with its product, hours per day, flush and tube type.',
      },
    },
    {
      id: 'frequencies',
      part: 'Map supplements and tube feeds',
      heading: 'Set the mealtimes for daily, BID and TID',
      text: 'Frequencies lists what each frequency means at your facility. For example, BID is Lunch and Dinner, and TID is Breakfast, Lunch and Dinner. Select Set or Change to pick the mealtimes.\n\nA frequency with no mealtimes is Not set, and supplement text with it waits in the Map this pop-up.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/frequencies.webp',
          width: 1280,
          height: 420,
        },
        clip: {
          src: '/help/ehr-supplements-tube-feeds/frequencies.mp4',
          width: 1280,
          height: 420,
          durationSec: 3.2,
        },
        alt: 'The Frequencies section listing DAILY, BID, TID, QID, HS and MEALS with their mealtimes.',
      },
    },
    {
      id: 'turn-on',
      part: 'Turn it on, change it or turn it off',
      heading: 'Ask Dietech support to turn it on',
      text: 'Dietech support turns supplements and tube feeds on for your facility, each one separately. You can see the settings under Frequencies. Tell support how your EHR stops an order: with a discontinue for that order, or by sending the full list of current orders each time.\n\nTo change either one later, call or open a ticket with support.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/turn-on.webp',
          width: 1280,
          height: 112,
        },
        alt: 'The facility\'s settings, shown read only: Supplements on, Tube feeds on, and the others off.',
      },
    },
    {
      id: 'turn-off',
      part: 'Turn it on, change it or turn it off',
      heading: 'Turning it off',
      text: 'Ask Dietech support to turn either one off at any time. Items already added stay on the resident as they are, and from then on your staff look after them.',
      media: {
        image: {
          src: '/help/ehr-supplements-tube-feeds/turn-off.webp',
          width: 1280,
          height: 462,
        },
        alt: 'A resident\'s Meal Pattern with the Juven supplement items, which stay when the feature is turned off.',
      },
    },
  ],
  tips: [
    'After the first few orders arrive, open a resident\'s Meal Pattern and check that each supplement landed at the mealtimes you expected.',
    'A supplement your EHR orders "daily" waits in the Map this pop-up until your facility picks which mealtime daily means. There is no default, on purpose.',
    'Check Added Items once a day, the same way you check Allergy Changes, so nobody is surprised by a new supplement or tube feed.',
  ],
  notes: [
    'Already given? It is not added twice. If your staff already gave the resident the same supplement, in any flavor, Dietech does not add another and never ends yours. A tube feed your staff already entered is left as it is, and Dietech does not add a second one.',
    'Dietech never deletes a supplement it added. It gives it an end date, so the meal pattern keeps its history.',
    'Supplements and tube feeds are turned on separately. Your facility can use one without the other.',
  ],
  related: ['map-ehr-text'],
  recorded: '2026-10-25',
};

export default article;
